import { hardCutBitrateBpsFor, type WebRtcOptimizationConfig } from '@playcastdotio/PeerCore';
import { MobileQualityExperiment, type MobileExperimentArm } from './MobileQualityExperiment';

export interface MobileSessionOptions {
  mobileExperiment?: { arm: MobileExperimentArm; constrainedTrial: boolean };
}

/** Session-only experiment caps composed with operator, recovery and retained ceilings. */
export class MobileSessionConfiguration {
  constructor(private readonly options: MobileSessionOptions = {}) {}

  public apply(base: WebRtcOptimizationConfig, isMobile: boolean, retainedFloor: boolean): WebRtcOptimizationConfig {
    const experiment = this.options.mobileExperiment;
    const currentMax = base.maxBitrate ?? 8_000_000;
    const limits = new MobileQualityExperiment({ arm: experiment?.arm ?? 'control' }).limits({
      isMobile, constrainedTrial: experiment?.constrainedTrial ?? false, currentMaxBitrateBps: currentMax,
      hardCutMaxBitrateBps: hardCutBitrateBpsFor(currentMax, { resolution: { width: 640, height: 360 }, frameRate: 30 }),
    });
    const config = { ...base };
    if (retainedFloor || limits.maxWidth !== undefined) {
      config.captureResolution = { width: Math.min(base.captureResolution?.width ?? 640, 640),
        height: Math.min(base.captureResolution?.height ?? 360, 360) };
    }
    if (limits.maxFramerate !== undefined) config.targetFrameRate = Math.min(base.targetFrameRate ?? 60, limits.maxFramerate);
    if (limits.maxBitrateBps !== undefined) {
      const cap = limits.maxBitrateBps;
      config.maxBitrate = Math.min(currentMax, cap);
      config.burstMaxBitrate = Math.min(base.burstMaxBitrate ?? currentMax, cap);
      config.sdpBitrate = this.clampHints(base.sdpBitrate, cap);
      config.burstSdpBitrate = this.clampHints(base.burstSdpBitrate, cap);
    }
    if (limits.audioMaxBitrateBps !== undefined) config.audioMaxBitrate = Math.min(base.audioMaxBitrate ?? Infinity, limits.audioMaxBitrateBps);
    if (limits.audioStereo !== undefined) config.audioStereo = limits.audioStereo;
    return config;
  }

  private clampHints(hints: WebRtcOptimizationConfig['sdpBitrate'], ceilingBps: number): WebRtcOptimizationConfig['sdpBitrate'] {
    if (!hints) return hints;
    const ceilingKbps = Math.max(1, Math.floor(ceilingBps / 1000));
    return { ...hints, minBitrate: Math.min(hints.minBitrate, ceilingKbps),
      startBitrate: Math.min(hints.startBitrate, ceilingKbps), maxBitrate: Math.min(hints.maxBitrate, ceilingKbps) };
  }
}
